import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Measure from "react-measure";
import { DataGrid, EmptyItem, Pager, ProgressIndicator } from "rhombus-react";
import { fetchUpdatesHistory } from "reducers/actions/ContentFeedActions";
import { UpdatesHistory } from "reducers/UpdatesHistory";
import { DEFAULT_ROW_HEIGHT, CREATIVE_ROW_COUNT_OPTIONS, MIN_GRID_HEIGHT } from "utils/constants";
import { formatRelativeTime, getErrorSummary } from "utils/UpdatesHistory";
import { isErrorStatus, FEED_RUN_STATUS_CONFIG } from "utils/FeedRunStatus";
import ErrorDetailsModal from "widgets/modals/contentfeeds/ErrorDetailsModal";

import "./UpdatesHistoryPage.css";

const TYPE_COLUMN = {
	key: "type",
	name: "Update Type",
	width: 150,
	formatter: ({ value }) => {
		const displayValue = value === "manual" ? "Manual" : "Automatic";
		return <span>{displayValue}</span>;
	},
};

const STATUS_COLUMN = {
	key: "status",
	name: "Result",
	width: 150,
	formatter: ({ value }) => {
		const { label, iconClass } = FEED_RUN_STATUS_CONFIG[value] || FEED_RUN_STATUS_CONFIG.error;
		return (
			<span>
				<i className={`${iconClass}`} style={{ marginRight: "8px" }} />
				{label}
			</span>
		);
	},
};

function InfoCell({ value, dependentValues }) {
	const [isModalOpen, setIsModalOpen] = useState(false);

	if (!isErrorStatus(dependentValues.status)) {
		return <span>-</span>;
	}

	const errors = value;
	const { prefix, codesSummary } = getErrorSummary(errors);

	const handleShowMore = (e) => {
		e.stopPropagation();
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	return (
		<div className="error-info-cell">
			<span className="error-summary">
				<strong>{prefix}</strong> {codesSummary}{" "}
			</span>
			<strong><a className="show-more-link" onClick={handleShowMore}>View details</a></strong>
			{isModalOpen && <ErrorDetailsModal errors={errors} onClose={handleCloseModal} />}
		</div>
	);
}

const INFO_COLUMN = {
	key: "error",
	name: "Info",
	width: 1000,
	formatter: InfoCell,
	getRowMetaData: row => row,
};

const START_TIME_COLUMN = {
	key: "startTime",
	name: "Time",
	formatter: ({ value }) => {
		const formattedDate = formatRelativeTime(value);
		return <span>{formattedDate}</span>;
	},
};

const UPDATES_HISTORY_COLUMNS = [
	TYPE_COLUMN,
	STATUS_COLUMN,
	INFO_COLUMN,
	START_TIME_COLUMN,
];

export default function UpdatesHistoryPage() {
	const { contentFeedId } = useParams();
	const dispatch = useDispatch();
	const {
		items: updatesHistory,
		isLoading,
		totalPages,
		totalItems,
		filterInfo: { page, rpp },
	} = useSelector(state => state.updatesHistory);

	useEffect(() => {
		if (contentFeedId) {
			dispatch(fetchUpdatesHistory(contentFeedId, page, rpp));
		}
	}, [contentFeedId, page, rpp]);

	useEffect(() => {
		return () => {
			dispatch(UpdatesHistory.clearUpdatesHistory());
		};
	}, []);

	function getRow(rowID) {
		if (rowID === -1) {
			return;
		}
		return updatesHistory[rowID];
	}

	const dataGridProps = {
		columns: UPDATES_HISTORY_COLUMNS,
		rowGetter: getRow,
		rowsCount: updatesHistory.length,
		rowHeight: DEFAULT_ROW_HEIGHT,
		enableCellAutoFocus: false,
	};

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: totalPages,
		maxItems: totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,
		disabled: isLoading,

		onPageChange: newPage => dispatch(UpdatesHistory.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(UpdatesHistory.setRPP(newRPP)),
	};

	if (!isLoading && updatesHistory.length === 0) {
		const emptyProps = {
			className: "empty-updates-history",
			title: "No Update History",
			message: "There is no update history available for this bulk sheet yet.",
			src: "/images/noFiles.svg",
		};

		return (
			<div className="updates-history-page">
				<EmptyItem {...emptyProps} />
			</div>
		);
	}

	return (
		<div className="updates-history-page">
			<div className="updates-history-content">
				<Measure>
					{(obj) => {
						const gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
						return (
							<div className="measure-component" ref={obj.measureRef}>
								<ProgressIndicator isLoading={isLoading} />
								<DataGrid
									{...dataGridProps}
									minHeight={gridHeight}
								/>
							</div>
						);
					}}
				</Measure>
			</div>
			{totalPages > 0 && (
				<div className="updates-history-footer">
					<Pager {...pagerProps} />
				</div>
			)}
		</div>
	);
}

UpdatesHistoryPage.displayName = "UpdatesHistoryPage";

